import {
  ICidade,
  ICodImovel,
  IConfiguracoes,
  IEstado,
  MetadataState
} from '@stores/Metadata/types';
import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { httpClient } from '@lib/httpClient';
import { useUserStore } from '@stores/User/User';

export const useMetadataStore = create<MetadataState>()(
  devtools(
    persist(
      (set) => ({
        tiposImovel: [],
        condicoesImovel: [],
        chaves: [],
        codigosImovel: [],
        adminTiposImovel: [],
        adminCodigosImovel: [],
        adminChaves: [],
        adminCondicoesImovel: [],
        adminCaracteristicas: [],
        adminEstados: [],
        adminCidades: [],
        configuracoes: null,
        estados: [],
        cidades: [],
        caracteristicas: [],

        fetchTiposImovel: async () => {
          try {
            const { data } = await httpClient.get('/api/imoveis/tipos');
            set({ tiposImovel: data });
          } catch {
            console.error('Failed to fetch tiposImovel');
          }
        },

        fetchCondicoesImovel: async () => {
          try {
            const { token, user } = useUserStore.getState();
            if (!token || !user) {
              return;
            }
            httpClient.defaults.headers.Authorization = `Bearer ${token}`;
            const { data } = await httpClient.get('/api/condicao');
            set({ condicoesImovel: data });
          } catch {
            console.error('Failed to fetch condicoesImovel');
          }
        },

        fetchChaves: async () => {
          try {
            const { token, user } = useUserStore.getState();
            if (!token || !user) {
              return;
            }
            httpClient.defaults.headers.Authorization = `Bearer ${token}`;
            const { data } = await httpClient.get('/api/chaves');
            console.log('Chaves fetched:', data);
            set({ chaves: data });
          } catch {
            console.error('Failed to fetch chaves');
          }
        },

        fetchCodigosImovel: async () => {
          try {
            const { token, user } = useUserStore.getState();
            if (!token || !user) {
              return;
            }
            httpClient.defaults.headers.Authorization = `Bearer ${token}`;
            const { data } = await httpClient.get('/api/codimovel');
            set({ codigosImovel: data });
          } catch {
            console.error('Failed to fetch codigosImovel');
          }
        },

        fetchAllCodigosImovel: async () => {
          try {
            const { token } = useUserStore.getState();
            if (!token) return;
            httpClient.defaults.headers.Authorization = `Bearer ${token}`;
            const { data } = await httpClient.get('/api/config/cod-imovel');
            set({ adminCodigosImovel: data });
          } catch {
            console.error('Failed to fetch all cod-imovel');
          }
        },

        fetchCidades: async () => {
          try {
            const { data }: { data: ICidade[] } =
              await httpClient.get('/api/cidades');
            const estados: IEstado[] = data.reduce(
              (acc: IEstado[], cidade: ICidade) => {
                if (!acc.find((estado) => estado.id === cidade.estado.id)) {
                  acc.push(cidade.estado);
                }
                return acc;
              },
              []
            );
            set({ estados, cidades: data });
          } catch {
            console.error('Failed to fetch cidades');
          }
        },

        fetchCaracteristicas: async () => {
          try {
            const { data } = await httpClient.get('/api/caracteristicas');
            set({ caracteristicas: data });
          } catch {
            console.error('Failed to fetch caracteristicas');
          }
        },

        addCodImovel: async (payload) => {
          try {
            const { token } = useUserStore.getState();
            if (!token) return null;
            httpClient.defaults.headers.Authorization = `Bearer ${token}`;
            const { data } = await httpClient.post(
              '/api/config/cod-imovel',
              payload
            );
            return data.cod_imovel as ICodImovel;
          } catch {
            console.error('Failed to add codImovel');
            return null;
          }
        },

        updateCodImovel: async (id, payload) => {
          try {
            const { token } = useUserStore.getState();
            if (!token) return false;
            httpClient.defaults.headers.Authorization = `Bearer ${token}`;
            await httpClient.patch(`/api/config/cod-imovel/${id}`, payload);
            return true;
          } catch {
            console.error('Failed to update codImovel');
            return false;
          }
        },

        removeCodImovel: async (id) => {
          try {
            const { token } = useUserStore.getState();
            if (!token) return false;
            httpClient.defaults.headers.Authorization = `Bearer ${token}`;
            await httpClient.delete(`/api/config/cod-imovel/${id}`);
            return true;
          } catch {
            console.error('Failed to remove codImovel');
            return false;
          }
        },

        reorderCodImoveis: async (ids) => {
          try {
            const { token } = useUserStore.getState();
            if (!token) return false;
            httpClient.defaults.headers.Authorization = `Bearer ${token}`;
            await httpClient.post('/api/config/cod-imovel/reorder', { ids });
            return true;
          } catch {
            console.error('Failed to reorder codImoveis');
            return false;
          }
        },

        fetchAllTiposImovel: async () => {
          try {
            const { token } = useUserStore.getState();
            if (!token) return;
            httpClient.defaults.headers.Authorization = `Bearer ${token}`;
            const { data } = await httpClient.get('/api/config/tipos-imovel');
            set({ adminTiposImovel: data });
          } catch {
            console.error('Failed to fetch all tipos-imovel');
          }
        },

        syncCodImoveis: async (items) => {
          try {
            const { token } = useUserStore.getState();
            if (!token) return false;
            httpClient.defaults.headers.Authorization = `Bearer ${token}`;
            await httpClient.patch('/api/config/cod-imovel', { items });
            return true;
          } catch {
            console.error('Failed to sync codImoveis');
            return false;
          }
        },

        fetchAllChaves: async () => {
          try {
            const { token } = useUserStore.getState();
            if (!token) return;
            httpClient.defaults.headers.Authorization = `Bearer ${token}`;
            const { data } = await httpClient.get('/api/config/chaves');
            set({ adminChaves: data });
          } catch {
            console.error('Failed to fetch all chaves');
          }
        },

        fetchAllCondicoesImovel: async () => {
          try {
            const { token } = useUserStore.getState();
            if (!token) return;
            httpClient.defaults.headers.Authorization = `Bearer ${token}`;
            const { data } = await httpClient.get('/api/config/condicoes');
            set({ adminCondicoesImovel: data });
          } catch {
            console.error('Failed to fetch all condicoes');
          }
        },

        fetchAllCaracteristicas: async () => {
          try {
            const { token } = useUserStore.getState();
            if (!token) return;
            httpClient.defaults.headers.Authorization = `Bearer ${token}`;
            const { data } = await httpClient.get(
              '/api/config/caracteristicas'
            );
            set({ adminCaracteristicas: data });
          } catch {
            console.error('Failed to fetch all caracteristicas');
          }
        },

        syncTiposImovel: async (items) => {
          try {
            const { token } = useUserStore.getState();
            if (!token) return false;
            httpClient.defaults.headers.Authorization = `Bearer ${token}`;
            await httpClient.patch('/api/config/tipos-imovel', { items });
            return true;
          } catch {
            console.error('Failed to sync tiposImovel');
            return false;
          }
        },

        syncChaves: async (items) => {
          try {
            const { token } = useUserStore.getState();
            if (!token) return false;
            httpClient.defaults.headers.Authorization = `Bearer ${token}`;
            await httpClient.patch('/api/config/chaves', { items });
            return true;
          } catch {
            console.error('Failed to sync chaves');
            return false;
          }
        },

        syncCondicoesImovel: async (items) => {
          try {
            const { token } = useUserStore.getState();
            if (!token) return false;
            httpClient.defaults.headers.Authorization = `Bearer ${token}`;
            await httpClient.patch('/api/config/condicoes', { items });
            return true;
          } catch {
            console.error('Failed to sync condicoesImovel');
            return false;
          }
        },

        syncCaracteristicas: async (items) => {
          try {
            const { token } = useUserStore.getState();
            if (!token) return false;
            httpClient.defaults.headers.Authorization = `Bearer ${token}`;
            await httpClient.patch('/api/config/caracteristicas', { items });
            return true;
          } catch {
            console.error('Failed to sync caracteristicas');
            return false;
          }
        },

        fetchAllEstadosCidades: async () => {
          try {
            const { token } = useUserStore.getState();
            if (!token) return;
            httpClient.defaults.headers.Authorization = `Bearer ${token}`;
            const { data } = await httpClient.get(
              '/api/config/cidades-estados'
            );
            set({ adminEstados: data.estados, adminCidades: data.cidades });
          } catch {
            console.error('Failed to fetch all cidades-estados');
          }
        },

        syncEstadosCidades: async (estados, cidades) => {
          try {
            const { token } = useUserStore.getState();
            if (!token) return false;
            httpClient.defaults.headers.Authorization = `Bearer ${token}`;
            await httpClient.patch('/api/config/cidades-estados', {
              estados,
              cidades
            });
            return true;
          } catch {
            console.error('Failed to sync cidades-estados');
            return false;
          }
        },

        fetchConfiguracoes: async () => {
          try {
            const { data } = await httpClient.get('/api/configuracoes');
            set({ configuracoes: data as IConfiguracoes });
          } catch {
            console.error('Failed to fetch configuracoes');
          }
        },

        updateConfiguracoes: async (payload) => {
          try {
            const { token } = useUserStore.getState();
            if (!token) return false;
            httpClient.defaults.headers.Authorization = `Bearer ${token}`;
            await httpClient.patch('/api/configuracoes', payload);
            return true;
          } catch {
            console.error('Failed to update configuracoes');
            return false;
          }
        },

        uploadConfiguracaoImagem: async (file) => {
          try {
            const { token } = useUserStore.getState();
            if (!token) return null;
            httpClient.defaults.headers.Authorization = `Bearer ${token}`;
            const form = new FormData();
            form.append('image', file);
            const { data } = await httpClient.post(
              '/api/config/configuracoes/imagem',
              form,
              { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            return data.url as string;
          } catch {
            console.error('Failed to upload configuracao image');
            return null;
          }
        }
      }),
      {
        name: 'metadata-store',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          tiposImovel: state.tiposImovel,
          condicoesImovel: state.condicoesImovel,
          chaves: state.chaves,
          codigosImovel: state.codigosImovel,
          estados: state.estados,
          cidades: state.cidades,
          caracteristicas: state.caracteristicas,
          configuracoes: state.configuracoes
        })
      }
    )
  )
);
